import {
  ProductStatus,
  type tempCreateProductInput,
  type TempCategoryInput,
  type VariationInput,
  type ProductCombinationInput,
} from '_graphql/__generated__/globalTypes';
import type {
  BulkChildRow,
  BulkProductRowVariation,
  BulkProductUploadLine,
  BulkProductUploadRow,
} from './parseBulkProductUpload';

function rowVariationsDefToApi(
  def: NonNullable<BulkProductUploadRow['variationsDef']>,
): VariationInput[] {
  return def.map((v) => ({
    id: v.id,
    variation_name: v.variation_name,
    variation_options: v.variation_options.map((o) => ({
      id: o.id,
      variation_value: o.variation_value,
    })),
  }));
}

function asBool(v: boolean | null | undefined): boolean | null | undefined {
  if (v === undefined) {
    return undefined;
  }
  if (v === null) {
    return null;
  }
  return Boolean(v);
}

function normalizeMediaUrls(avatar: string, media: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of [avatar, ...media]) {
    const u = String(raw ?? '').trim();
    if (u && !seen.has(u)) {
      seen.add(u);
      out.push(u);
    }
  }
  return out;
}

function buildCategoriesForRow(
  row: BulkProductUploadRow,
  categoryIdToName: Map<string, string>,
): (TempCategoryInput | null)[] | null | undefined {
  if (!row.categoryId) {
    return undefined;
  }
  const name = categoryIdToName.get(row.categoryId);
  if (!name) {
    return undefined;
  }
  return [
    {
      name,
      description: null,
    },
  ];
}

function applyProductMetadata(
  input: tempCreateProductInput,
  row: BulkProductUploadRow,
  categoryIdToName: Map<string, string>,
): void {
  const limited = asBool(row.limited_stock);
  if (limited !== undefined) {
    input.limited_stock = limited;
  }
  const inhouse = asBool(row.inhouse);
  if (inhouse !== undefined) {
    input.inhouse = inhouse;
  }
  if (row.weight != null) {
    input.weight = row.weight;
  }
  if (row.tags != null && row.tags.length > 0) {
    input.tags = row.tags;
  }
  if (row.catalogIds != null && row.catalogIds.length > 0) {
    input.catalog_id = row.catalogIds;
  }
  const categories = buildCategoriesForRow(row, categoryIdToName);
  if (categories != null) {
    input.categories = categories;
  }
}

function singleRowToInput(
  row: BulkProductUploadRow,
  categoryIdToName: Map<string, string>,
): tempCreateProductInput {
  const media = normalizeMediaUrls(row.avatar, row.media ?? []);
  const input: tempCreateProductInput = {
    uuid: row.uuid,
    title: row.title.trim(),
    sku: row.sku.trim(),
    description: row.description.trim(),
    currency: row.currency,
    country: row.country,
    published: Boolean(row.published),
    price: row.price,
    cost_price: row.costPrice,
    avatar: row.avatar.trim(),
    // shop_id: row.shopId,
    media,
    qty: row.quantity,
    status: ProductStatus.ACCEPTED,
  };
  if (row.variationsDef != null && row.variationsDef.length > 0) {
    input.variations = rowVariationsDefToApi(row.variationsDef);
  }
  applyProductMetadata(input, row, categoryIdToName);
  return input;
}

function childRowsToProductCombinations(
  parentSku: string,
  children: { row: BulkChildRow; excelRow: number }[],
  parent: BulkProductUploadRow,
): ProductCombinationInput[] {
  const combinationCategories: (string | null)[] | null = parent.categoryId
    ? [parent.categoryId]
    : null;
  return children.map((c, idx) => ({
    id: crypto.randomUUID(),
    sku: `${parentSku.trim()}-${idx + 1}`,
    price: c.row.price,
    cost_price: c.row.costPrice,
    qty: c.row.quantity,
    combination_string: c.row.combinationString.trim(),
    avatar: c.row.avatar.trim(),
    media: null,
    categories: combinationCategories,
  } satisfies ProductCombinationInput));
}

function validateCombinationStringAgainstVariations(
  variations: BulkProductRowVariation[],
  combinationString: string,
  parentSku: string,
  childExcelRow: number,
): string | null {
  const t = combinationString.trim();
  if (variations.length === 0) {
    return null;
  }
  if (!t) {
    return `Parent ${parentSku}: empty COMBINATION_STRING (combination line row ${childExcelRow})`;
  }
  const parts = t.split('&').map((p) => p.trim());
  if (parts.length !== variations.length) {
    return `Parent ${parentSku}: combination "${t}" has ${parts.length} segment(s) separated by "&" but the parent defines ${variations.length} variation axis/axes (row ${childExcelRow})`;
  }
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]!;
    const axis = variations[i]!;
    const allowed = new Set(
      axis.variation_options.map((o) => o.variation_value.trim()),
    );
    if (!allowed.has(part)) {
      return `Parent ${parentSku}: combination "${t}" is invalid: "${part}" is not an option for "${axis.variation_name}" (row ${childExcelRow})`;
    }
  }
  return null;
}

function parentWithChildrenToInput(
  parent: BulkProductUploadRow,
  childEntries: { row: BulkChildRow; excelRow: number }[],
  categoryIdToName: Map<string, string>,
): tempCreateProductInput {
  const pSku = parent.sku.trim();
  const variations: VariationInput[] =
    parent.variationsDef && parent.variationsDef.length > 0
      ? rowVariationsDefToApi(parent.variationsDef)
      : [];

  const combinations = childRowsToProductCombinations(
    pSku,
    childEntries,
    parent,
  );

  const parentMedia = normalizeMediaUrls(parent.avatar, parent.media ?? []);
  const input: tempCreateProductInput = {
    uuid: parent.uuid,
    title: parent.title.trim(),
    sku: pSku,
    description: parent.description.trim(),
    currency: parent.currency,
    country: parent.country,
    published: Boolean(parent.published),
    price: parent.price,
    cost_price: parent.costPrice,
    avatar: parent.avatar.trim(),
    // shop_id: parent.shopId,
    media: parentMedia,
    qty: parent.quantity,
    status: ProductStatus.ACCEPTED,
    variations: variations.length > 0 ? variations : undefined,
    combinations: combinations.length > 0 ? combinations : undefined,
  };
  applyProductMetadata(input, parent, categoryIdToName);
  return input;
}

export type BulkRowsToInputResult =
  | { ok: true; products: tempCreateProductInput[] }
  | { ok: false; errors: string[] };

export function bulkRowsToTempCreateProductInputs(
  lines: BulkProductUploadLine[],
  categoryIdToName: Map<string, string>,
): BulkRowsToInputResult {
  const parentSkus = new Set(
    lines
      .filter(
        (
          l
        ): l is {
          type: 'parent';
          row: BulkProductUploadRow;
          excelRow: number;
        } => l.type === 'parent',
      )
      .map((l) => l.row.sku.trim()),
  );

  const seenParent = new Set<string>();
  for (const l of lines) {
    if (l.type === 'parent') {
      const s = l.row.sku.trim();
      if (seenParent.has(s)) {
        return {
          ok: false,
          errors: [
            `Duplicate parent product row for SKU "${s}". Use one parent row and combination lines (PARENT_SKU) for variants.`,
          ],
        };
      }
      seenParent.add(s);
    }
  }

  const childrenByParent = new Map<
    string,
    { row: BulkChildRow; excelRow: number }[]
  >();
  for (const l of lines) {
    if (l.type !== 'child') {
      continue;
    }
    const key = l.row.parentSku.trim();
    if (!parentSkus.has(key)) {
      return {
        ok: false,
        errors: [
          `Row ${l.excelRow}: PARENT_SKU "${key}" does not match any parent product SKU in the file.`,
        ],
      };
    }
    const list = childrenByParent.get(key) ?? [];
    list.push({ row: l.row, excelRow: l.excelRow });
    childrenByParent.set(key, list);
  }

  for (const l of lines) {
    if (l.type !== 'parent') {
      continue;
    }
    const parent = l.row;
    const pSku = parent.sku.trim();
    const children = childrenByParent.get(pSku) ?? [];
    const vDef = parent.variationsDef;

    if (vDef && vDef.length > 0) {
      if (children.length === 0) {
        return {
          ok: false,
          errors: [
            `Product "${pSku}": add combination rows (PARENT_SKU, COMBINATION_STRING) for each option mix; variation axes are defined on the parent row only.`,
          ],
        };
      }
      const seenCombo = new Set<string>();
      for (const c of children) {
        const cs = c.row.combinationString.trim();
        if (seenCombo.has(cs)) {
          return {
            ok: false,
            errors: [
              `Product "${pSku}": duplicate COMBINATION_STRING "${cs}" (row ${c.excelRow}). Each combination must be unique.`,
            ],
          };
        }
        seenCombo.add(cs);
        const vErr = validateCombinationStringAgainstVariations(
          vDef,
          c.row.combinationString,
          pSku,
          c.excelRow,
        );
        if (vErr) {
          return { ok: false, errors: [vErr] };
        }
      }
    } else if (children.length > 0) {
      return {
        ok: false,
        errors: [
          `Product "${pSku}": combination (PARENT_SKU) lines require VARIATION_NAME_n / VARIATION_OPTIONS_n on the parent row.`,
        ],
      };
    }
  }

  const parentLineOrder = lines.filter(
    (
      e
    ): e is { type: 'parent'; row: BulkProductUploadRow; excelRow: number } =>
      e.type === 'parent',
  );
  const products: tempCreateProductInput[] = parentLineOrder.map((pl) => {
    const pSku = pl.row.sku.trim();
    const children = childrenByParent.get(pSku) ?? [];
    if (children.length > 0) {
      return parentWithChildrenToInput(
        pl.row,
        children,
        categoryIdToName,
      );
    }
    return singleRowToInput(pl.row, categoryIdToName);
  });

  return { ok: true, products };
}
